import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../data/DataProvider';
import { date } from '../../shared/formatters';
import { openProtectedFile } from '../../lib/pb';
import SearchInput from '../../shared/components/SearchInput';
import EmptyState from '../../shared/components/EmptyState';
import Skeleton from '../../shared/components/Skeleton';
import CertificateForm from './CertificateForm';
import CertificateWizard from '../../engineer/certificates/CertificateWizard';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';

const DAY = 86400000;

// Compliance state computed from expiry_date (never stored) — same contract
// as the pass-5 plan §5F: valid / expiring soon (≤30d) / expired.
function certState(expiryDate) {
  if (!expiryDate) return { key: 'valid', label: 'No expiry', pill: 'paid' };
  const days = Math.round((new Date(expiryDate) - Date.now()) / DAY);
  if (days < 0) return { key: 'expired', label: `Expired ${Math.abs(days)}d ago`, pill: 'overdue' };
  if (days <= 30) return { key: 'expiring', label: `Expires in ${days}d`, pill: 'pending' };
  return { key: 'valid', label: `Valid · ${days}d left`, pill: 'paid' };
}

export default function CertificatesOverview() {
  const { data } = useData();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [form, setForm] = useState(false);
  const [gasWizard, setGasWizard] = useState(false);
  const [gasType, setGasType] = useState('lgsr');
  const [gasClientId, setGasClientId] = useState('');
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 200);
    return () => clearTimeout(timer);
  }, []);

  const sorted = useMemo(() => [...data.certificates].sort((a, b) => {
    // Soonest-to-expire first; no-expiry last.
    if (!a.expiry_date) return 1;
    if (!b.expiry_date) return -1;
    return new Date(a.expiry_date) - new Date(b.expiry_date);
  }), [data.certificates]);

  const filtered = useMemo(() => sorted.filter((cert) => {
    const client = data.clients.find((item) => item.id === cert.client_id);
    const haystack = `${client?.name || ''} ${cert.certificate_number} ${cert.typeLabel}`.toLowerCase();
    return !query || haystack.includes(query.toLowerCase());
  }), [sorted, data.clients, query]);

  const expiringCount = sorted.filter((cert) => cert.expiry_date && new Date(cert.expiry_date) - Date.now() < 30 * DAY).length;

  const open = async (cert) => {
    setOpening(cert.id);
    try {
      await openProtectedFile(cert, cert.filename);
    } catch {
      // browser surfaces the error; nothing to toast
    } finally {
      setOpening(null);
    }
  };

  if (loading) return <div className="standalone-page"><Skeleton rows={6} /></div>;

  return (
    <div className="standalone-page">
      <header className="simple-header">
        <div>
          <span className="eyebrow">Compliance</span>
          <h1>Certificates</h1>
          <p className="field-hint">{expiringCount} expiring or expired within 30 days · soonest first.</p>
        </div>
        <div className="header-actions">
          <button className="primary-button" onClick={() => setForm(true)}>+ Add certificate</button>
          <button className="secondary-button" onClick={() => { setGasType('lgsr'); setGasClientId(''); setGasWizard(true); }}>+ New Gas Certificate</button>
        </div>
      </header>

      {gasWizard && (
        <div className="panel gas-wizard-launcher">
          <h4>Create Gas Certificate</h4>
          <div className="form-row">
            <label>Type
              <select value={gasType} onChange={(e) => setGasType(e.target.value)}>
                <option value="lgsr">Landlord Gas Safety (CP12)</option>
                <option value="gsr">Gas Safety Record</option>
                <option value="gsirep">Safety Inspection Report</option>
                <option value="gwn">Warning Advice Notice</option>
                <option value="gmsc">Service Maintenance Checklist</option>
                <option value="gbsr">Breakdown/Servicing Record</option>
                <option value="wpcr">Pipework Commissioning Record</option>
              </select>
            </label>
            <label>Client
              <select value={gasClientId} onChange={(e) => setGasClientId(e.target.value)}>
                <option value="">Choose a client…</option>
                {data.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <button className="primary-button" onClick={() => setGasWizard(false)}>Close</button>
          </div>
          {gasClientId && (
            <CertificateWizard
              certificateType={gasType}
              client={data.clients.find((c) => c.id === gasClientId)}
              allowUnsigned
              redirectTo={null}
              onSave={() => {
                setGasWizard(false);
                setGasClientId('');
              }}
              onCancel={() => setGasWizard(false)}
            />
          )}
        </div>
      )}

      <div className="toolbar">
        <SearchInput value={query} onChange={setQuery} placeholder="Search client, certificate number or type…" />
      </div>

      {!data.certificates.length ? (
        <EmptyState title="No certificates yet" message="Add a certificate from a client record or here — expiries sync a renewal key date automatically." action="Add certificate" onAction={() => setForm(true)} />
      ) : !filtered.length ? (
        <EmptyState title="No matching certificates" message="Try changing your search." />
      ) : (
        <section className="panel no-padding invoice-list">
          {filtered.map((cert) => {
            const client = data.clients.find((item) => item.id === cert.client_id);
            const state = certState(cert.expiry_date);
            return (
              <div className="invoice-row" key={cert.id}>
                <div>
                  <strong>{cert.certificate_number || cert.typeLabel}</strong>
                  <span>{cert.typeLabel}{cert.notes ? ` · ${cert.notes}` : ''}</span>
                </div>
                <button type="button" className="link-button" onClick={() => navigate(`/admin/clients/${cert.client_id}`)}>{client?.name || 'Unknown client'}</button>
                <span>Issued {date(cert.issued_date)}</span>
                <span>Expires {date(cert.expiry_date)}</span>
                <span className={`status-pill status-${state.pill}`}><span className="status-dot" />{state.label}</span>
                {cert.filename ? (
                  <button type="button" className="link-button" onClick={() => open(cert)} disabled={opening === cert.id}>
                    <SafeIcon icon={Icons.Download} /> {opening === cert.id ? 'Opening…' : 'View'}
                  </button>
                ) : null}
              </div>
            );
          })}
        </section>
      )}

      <CertificateForm
        open={form}
        onClose={() => setForm(false)}
        onSaved={() => undefined}
      />
    </div>
  );
}
